"use client";

import { useEffect, useRef, useState } from "react";
import { PortableText } from "@portabletext/react";
import { stegaClean } from "@sanity/client/stega";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { CTAButton } from "@/shadcn/ui/button";
import type {
  AiWorkflowCard,
  PageAiFeaturesSchema,
} from "@/sanity/pages/PageAiFeaturesTemplate";
import { SectionHeading } from "./ui";
import DemoMedia from "./DemoMedia";
import {
  MorningDemo,
  LiveDemo,
  ReviewDemo,
  CompanionDemo,
  WORKFLOW_MOMENTS,
  type WorkflowMoment,
} from "./ProductDemos";
import styles from "./ai-features.module.scss";

function workflowMoment(
  card: AiWorkflowCard | undefined,
  index: number,
): WorkflowMoment {
  const configured = stegaClean(card?.demoKind);
  if (configured && WORKFLOW_MOMENTS.includes(configured)) return configured;
  for (const image of [card?.image, card?.mobileImage]) {
    for (const source of [image?.src, image?.name]) {
      const match = stegaClean(source || "").match(
        /(?:^|\/)(morning|live|review)(?:-mobile)?-[234]x\.webp(?:[?#]|$)/,
      );
      if (match) return match[1] as WorkflowMoment;
    }
  }
  // Preserve older custom-media documents until their scene is explicitly selected.
  return WORKFLOW_MOMENTS[index] || "review";
}

const DEMOS = { morning: MorningDemo, live: LiveDemo, review: ReviewDemo };
const DEMO_LABELS = {
  morning: "Morning brief",
  live: "Live session",
  review: "Weekly review",
};

export default function WorkflowSection({
  data,
}: {
  data?: PageAiFeaturesSchema["workflow"];
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const cards = data?.cards ?? [];
  const cardKeys = cards.map((card) => card._key).join("|");
  useEffect(() => {
    const elements = ref.current?.querySelectorAll("[data-card]");
    if (!elements?.length) {
      setActive(0);
      return;
    }
    setActive((current) => Math.min(current, elements.length - 1));
    const intersections = new Map<Element, IntersectionObserverEntry>();
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => intersections.set(entry.target, entry));
        const visible = [...intersections.values()]
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible)
          setActive(Number((visible.target as HTMLElement).dataset.card));
      },
      { rootMargin: "-15% 0px -35% 0px", threshold: [0, 0.15, 0.3, 0.5, 0.8] },
    );
    elements.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, [cardKeys]);
  if (!data) return null;
  return (
    <section id="workflow" className={styles.workflow}>
      <div className="px-[3rem]">
        <SectionHeading data={data} />
      </div>
      <div ref={ref} className={styles.workflowBody}>
        <div className={styles.workflowCards}>
          {cards.map((card, i) => {
            const moment = workflowMoment(card, i);
            const Demo = DEMOS[moment];
            return (
              <article
                key={card._key}
                data-card={i}
                className={styles.workflowCard}
              >
                <DemoMedia
                  label={DEMO_LABELS[moment]}
                  mode={card.mediaType}
                  video={card.video}
                  image={card.image}
                  mobileImage={card.mobileImage}
                  sizes="(max-width: 1023px) 92vw, 53vw"
                  className={styles.workflowMedia}
                >
                  <Demo />
                </DemoMedia>
                <div className={styles.cardMeta}>
                  <div>
                    <p className={styles.cardLabel}>{card.label}</p>
                    {card.title && <h3>{card.title}</h3>}
                    {card.description?.length ? (
                      <div className={styles.cardDescription}>
                        <PortableText
                          value={card.description}
                          components={richTextComponent}
                        />
                      </div>
                    ) : null}
                  </div>
                  {card.cta && <CTAButton data={card.cta} showIncomplete />}
                </div>
              </article>
            );
          })}
        </div>
        <aside
          className={styles.companion}
          aria-label="TS Coach daily workflow preview"
        >
          <DemoMedia
            label="TS Coach companion"
            mode={cards[active]?.sidebarMediaType}
            video={cards[active]?.sidebarVideo}
            image={cards[active]?.sidebarImage}
            sizes="21vw"
          >
            <CompanionDemo moment={workflowMoment(cards[active], active)} />
          </DemoMedia>
        </aside>
      </div>
    </section>
  );
}
